// When a hover card is up. The pure half of `HoverCard.tsx`.
//
// **A tooltip is a state machine and the bugs are all in its edges**, which is
// why this is a fold over events rather than four `setTimeout`s scattered
// through a component: the pointer crossing the gap between a trigger and its
// card, a card left open because the pointer went straight from the trigger to
// something else, Escape closing one that reopens the instant the pointer
// twitches. Each is one line here and a test below it.
//
// Two timers, never more. **`open`** is the delay before a hover means
// anything — the difference between explaining a control and strobing every
// control the pointer crosses on its way somewhere. **`close`** is a debounce
// against a jittery leave, and nothing else.
//
// **A card never holds itself open** (designer ruling, U6 review 2026-09-02).
// The grace used to bridge the gap to the card, whose own `pointerenter`
// cancelled it — so a card the player was trying to reach *past* stayed up and
// took the click. The surface is `pointer-events: none` now, `enter` and
// `leave` are the trigger's alone, and leaving the trigger closes the card
// whatever the pointer is over.
//
// `dismissed` is the latch that stops Escape being useless. Without it the key
// closes a card the pointer is still sitting on and the very next thing the
// state machine hears is nothing at all — the pointer has not moved, so no
// `enter` arrives, so nothing reopens it. With it the intent survives until
// the pointer genuinely goes somewhere else. **`press` latches for the same
// reason**: a click on a trigger is the player reaching the control rather
// than reading about it, so the sentence gets out of the way and stays out of
// it until the pointer leaves and comes back.

/** What the component should have running. */
export type HoverTimer = "open" | "close" | null;

export type HoverEvent =
  /** Pointer arrived on the trigger. Never the card — it takes no pointer. */
  | { kind: "enter" }
  /** Pointer left the trigger. */
  | { kind: "leave" }
  /** Keyboard focus reached the trigger. */
  | { kind: "focus" }
  | { kind: "blur" }
  /** The `open` delay elapsed. */
  | { kind: "delay" }
  /** The `close` grace elapsed. */
  | { kind: "grace" }
  /** Escape, or a click away. */
  | { kind: "escape" }
  /** A fine-pointer press on the trigger. The player is using the control. */
  | { kind: "press" }
  /** A coarse-pointer tap on the trigger. */
  | { kind: "tap" }
  /** The trigger went disabled: a card that can never open. */
  | { kind: "off" };

export interface HoverState {
  /** The card is up. */
  open: boolean;
  /** The pointer is on the trigger. */
  over: boolean;
  /** The trigger holds keyboard focus. */
  focused: boolean;
  timer: HoverTimer;
  /** Put away on purpose. Cleared when the pointer leaves for real. */
  dismissed: boolean;
}

export const RESTING: HoverState = {
  open: false,
  over: false,
  focused: false,
  timer: null,
  dismissed: false
};

export function hoverStep(state: HoverState, event: HoverEvent): HoverState {
  switch (event.kind) {
    case "enter":
      // Already up: the pointer came back inside the debounce, so cancel the
      // grace that was about to take it down. Dismissed: stay down — the
      // pointer has not left yet, so this is the same hover the press or the
      // Escape already answered.
      if (state.open || state.dismissed) return { ...state, over: true, timer: null };
      return { ...state, over: true, timer: "open" };

    case "leave":
      // **The dismissal ends here.** Leaving is the gesture that says the next
      // hover is a new question.
      if (state.open) {
        return { ...state, over: false, dismissed: false, timer: state.focused ? null : "close" };
      }
      return { ...state, over: false, dismissed: false, timer: null };

    // Keyboard focus opens at once. A player tabbing to a control has already
    // spent the deliberation the hover delay is there to wait for.
    case "focus":
      return { ...state, focused: true, open: true, timer: null, dismissed: false };

    case "blur":
      if (state.over) return { ...state, focused: false };
      return { ...state, focused: false, open: false, timer: null, dismissed: false };

    case "delay":
      // The pointer may have left while the delay ran, in which case `leave`
      // already cancelled the timer — but a stale fire must not open anything.
      if (!state.over || state.dismissed) return { ...state, timer: null };
      return { ...state, open: true, timer: null };

    case "grace":
      if (state.over || state.focused) return { ...state, timer: null };
      return { ...state, open: false, timer: null };

    // One outcome, two gestures. Escape is "stop telling me"; `press` is "I am
    // pressing this" — and a card that stayed up over the control being used
    // is the thing the ruling was written against.
    case "escape":
    case "press":
      return { ...state, open: false, timer: null, dismissed: true };

    // A tap toggles, because a coarse pointer has no hover to open with and no
    // leave to close with. Closing latches `dismissed` for the same reason
    // Escape does — the finger is still on the trigger.
    case "tap":
      if (state.open) return { ...state, open: false, timer: null, dismissed: true };
      return { ...state, open: true, timer: null, dismissed: false };

    // Where the pointer is stays true; everything the card was doing stops.
    case "off":
      return { ...RESTING, over: state.over, focused: state.focused };
  }
}

/** The fold. What a sequence of events leaves the card in. */
export function hoverState(events: readonly HoverEvent[]): HoverState {
  return events.reduce(hoverStep, RESTING);
}
